import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, X, ChevronLeft } from 'lucide-react';
import { getKnowledgeForPath } from '../utils/ajudanteKnowledge';

const INACTIVITY_MS = 60 * 1000;
const SILENCE_AFTER_NUDGE_MS = 10 * 60 * 1000;
const SILENCE_AFTER_INTERACTION_MS = 30 * 60 * 1000;
const NUDGE_MESSAGE = 'Tudo bem aí? Está com alguma dúvida? Posso te ajudar em algo?';

const HIDDEN_ROUTES = ['/', '/auth'];

function Ajudante() {
  const location = useLocation();
  const hidden = HIDDEN_ROUTES.includes(location.pathname);

  const knowledge = getKnowledgeForPath(location.pathname);

  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [showNudge, setShowNudge] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const silentUntilRef = useRef(0);
  const firedOnPageRef = useRef(false);
  const inactivityTimerRef = useRef(null);

  const isModalOpen = useCallback(() => {
    const dialogs = document.querySelectorAll('[role="dialog"]');
    return Array.from(dialogs).some((el) => el.getAttribute('data-state') !== 'closed');
  }, []);

  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const fireNudge = useCallback(() => {
    if (dismissed) return;
    if (firedOnPageRef.current) return;
    if (Date.now() < silentUntilRef.current) return;
    if (panelOpen) return;
    if (isModalOpen()) return;

    firedOnPageRef.current = true;
    setShowNudge(true);
    silentUntilRef.current = Date.now() + SILENCE_AFTER_NUDGE_MS;
  }, [dismissed, panelOpen, isModalOpen]);

  const resetInactivity = useCallback(() => {
    clearInactivityTimer();
    if (dismissed || hidden || panelOpen) return;
    inactivityTimerRef.current = setTimeout(fireNudge, INACTIVITY_MS);
  }, [dismissed, hidden, panelOpen, fireNudge]);

  // Reset "fired on page" flag when route changes.
  useEffect(() => {
    firedOnPageRef.current = false;
    setShowNudge(false);
    setPanelOpen(false);
    setSelectedIdx(null);
  }, [location.pathname]);

  // Listen for user activity to reset inactivity timer.
  useEffect(() => {
    if (hidden || dismissed) return undefined;

    const handler = () => {
      if (showNudge) setShowNudge(false);
      resetInactivity();
    };

    const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
    resetInactivity();

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handler));
      clearInactivityTimer();
    };
  }, [hidden, dismissed, resetInactivity, showNudge]);

  const openPanel = () => {
    setShowNudge(false);
    setPanelOpen(true);
    setSelectedIdx(null);
    silentUntilRef.current = Date.now() + SILENCE_AFTER_INTERACTION_MS;
    clearInactivityTimer();
  };

  const closePanel = () => {
    setPanelOpen(false);
    setSelectedIdx(null);
    resetInactivity();
  };

  const dismissForSession = () => {
    setDismissed(true);
    setShowNudge(false);
    setPanelOpen(false);
    clearInactivityTimer();
  };

  if (hidden || dismissed) return null;

  const selected =
    knowledge && selectedIdx !== null ? knowledge.questions[selectedIdx] : null;

  return (
    <>
      {/* Speech bubble nudge */}
      {showNudge && !panelOpen && (
        <div
          className="fixed bottom-28 right-4 z-[60] max-w-[280px] animate-in fade-in slide-in-from-bottom-2 duration-300"
          data-testid="ajudante-nudge"
        >
          <div className="relative bg-white rounded-2xl shadow-xl border-2 border-amber-300 p-4 pr-9">
            <button
              onClick={() => setShowNudge(false)}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-full"
              aria-label="Fechar aviso"
            >
              <X size={16} />
            </button>
            <p className="text-base text-[#1B2A4A] leading-snug">{NUDGE_MESSAGE}</p>
            <div className="absolute -bottom-2 right-8 w-4 h-4 bg-white border-r-2 border-b-2 border-amber-300 rotate-45" />
          </div>
        </div>
      )}

      {/* Floating bubble */}
      {!panelOpen && (
        <button
          onClick={openPanel}
          aria-label="Abrir Ajudante"
          data-testid="ajudante-bubble"
          className={`fixed bottom-24 right-4 z-[55] w-16 h-16 rounded-full bg-amber-400 hover:bg-amber-500 text-[#1B2A4A] shadow-lg flex items-center justify-center transition-all active:scale-95 border-4 border-white ${
            showNudge ? 'animate-pulse' : ''
          }`}
        >
          <HelpCircle size={32} strokeWidth={2.25} />
        </button>
      )}

      {/* Help panel */}
      {panelOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[70] animate-in fade-in duration-200"
            onClick={closePanel}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-label="Ajudante"
            data-testid="ajudante-panel"
            className="fixed left-0 right-0 bottom-0 z-[71] bg-[#FDFBF7] rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col animate-in slide-in-from-bottom duration-300 md:left-auto md:right-4 md:bottom-4 md:w-[420px] md:rounded-3xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-amber-400 rounded-t-3xl md:rounded-t-3xl border-b-2 border-amber-500">
              <div className="flex items-center gap-2 min-w-0">
                {selected !== null && (
                  <button
                    onClick={() => setSelectedIdx(null)}
                    aria-label="Voltar"
                    className="w-11 h-11 flex items-center justify-center bg-white/60 hover:bg-white rounded-xl text-[#1B2A4A] transition-all active:scale-95 shrink-0"
                  >
                    <ChevronLeft size={24} />
                  </button>
                )}
                <h2 className="text-xl font-bold text-[#1B2A4A] truncate">
                  Ajudante 💛
                </h2>
              </div>
              <button
                onClick={dismissForSession}
                aria-label="Fechar Ajudante"
                className="w-11 h-11 flex items-center justify-center bg-white/60 hover:bg-white rounded-xl text-[#1B2A4A] transition-all active:scale-95 shrink-0"
              >
                <X size={24} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-5 py-5 flex-1">
              {!knowledge && (
                <p className="text-base text-slate-700 leading-relaxed">
                  Ainda não tenho dicas para esta tela. Toque nos menus lá embaixo
                  para navegar entre as telas e me chame de novo quando quiser.
                </p>
              )}

              {knowledge && selected === null && (
                <>
                  <p className="text-base text-slate-700 mb-4 leading-relaxed">
                    Olá! Aqui estão algumas perguntas sobre a tela{' '}
                    <span className="font-semibold text-[#1B2A4A]">
                      {knowledge.title}
                    </span>
                    . Toque em uma para ver a resposta.
                  </p>
                  <div className="flex flex-col gap-3">
                    {knowledge.questions.map((item, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedIdx(i)}
                        className="text-left bg-white hover:bg-amber-50 border-2 border-slate-200 hover:border-amber-400 rounded-2xl px-4 py-4 min-h-[56px] text-base font-medium text-[#1B2A4A] transition-all active:scale-[0.99] shadow-sm"
                      >
                        {item.q}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {knowledge && selected && (
                <div>
                  <h3 className="text-lg font-bold text-[#1B2A4A] mb-3 leading-snug">
                    {selected.q}
                  </h3>
                  <div className="bg-white border-2 border-amber-200 rounded-2xl p-4 shadow-sm">
                    <p className="text-base text-slate-800 leading-relaxed whitespace-pre-line">
                      {selected.a}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedIdx(null)}
                    className="mt-5 w-full bg-[#1B2A4A] hover:bg-[#2C3E50] text-white font-semibold py-4 rounded-2xl min-h-[56px] text-base transition-all active:scale-[0.99]"
                  >
                    Ver outras perguntas
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Ajudante;
